import type { Dimension, Question, QuestionInputSettings, QuestionInputType, Widget } from '@dashboard/types/config'
import { idPrefixes, makeId } from '@dashboard/services/uuid'

export type QuestionTypeId =
  | 'mc'
  | 'yesno'
  | 'name'
  | 'email'
  | 'phone'
  | 'address'
  | 'country'
  | 'url'
  | 'short'
  | 'long'
  | 'number'
  | 'currency'
  | 'date'
  | 'rating'
  | 'nps'
  | 'slider'
export type QuestionTypeGroup = 'segment' | 'contact' | 'text' | 'scales'
export interface QuestionDraft {
  question: Question
  dimension: Dimension | null
}
export interface QuestionTypeDefinition {
  id: QuestionTypeId | 'legacy'
  label: string
  icon: string
  group: QuestionTypeGroup
  description: string
  facts: readonly string[]
  keywords?: readonly string[]
  inputType: QuestionInputType | null
  defaults: QuestionInputSettings
  text: string
  answers?: readonly string[]
}

const multipleChoice: QuestionTypeDefinition = {
  id: 'mc',
  label: 'Multiple choice',
  icon: 'list-bullet',
  group: 'segment',
  inputType: null,
  description:
    'Visitors pick one answer, or several if you allow it. Show answers as buttons, radio, images or a dropdown.',
  keywords: ['radio', 'checkbox'],
  facts: ['One segment per answer', 'One branch per answer in flows', 'Optional "Other" write-in'],
  text: 'What brings you here today?',
  answers: ['Explore my options', 'Get advice', 'Get started'],
  defaults: {}
}
const legacy: QuestionTypeDefinition = {
  ...multipleChoice,
  id: 'legacy',
  label: 'Open-ended (legacy)',
  icon: 'chat-bubble-left-right',
  description: 'A choice question with a write-in follow-up.',
  facts: ['Existing answers and write-ins are preserved']
}
export const questionTypes: readonly QuestionTypeDefinition[] = [
  multipleChoice,
  {
    ...multipleChoice,
    id: 'yesno',
    label: 'Yes / No',
    icon: 'hand-thumb-up',
    description: 'A quick two-answer question.',
    facts: ['Yes and No segments', 'Two branches in flows'],
    text: 'Would you like to learn more?',
    answers: ['Yes', 'No']
  },
  {
    id: 'name',
    label: 'Name',
    icon: 'user',
    group: 'contact',
    inputType: 'name',
    description: 'First and last name, or one full name field.',
    facts: ['Kept in Responses for export', 'Can sync first and last name to your email platform'],
    text: "What's your name?",
    defaults: { nameFormat: 'first_last', required: false }
  },
  {
    id: 'email',
    label: 'Email',
    icon: 'envelope',
    group: 'contact',
    inputType: 'email',
    description: 'An email address that can identify the visitor.',
    facts: ['Identifies the visitor', 'Finds or creates their contact', 'Suggests fixes for email typos'],
    text: "What's your email address?",
    defaults: {
      placeholder: 'name@example.com',
      required: true,
      identify: true,
      suggestTypoFixes: true,
      consent: {
        disclosure: { enabled: false, text: '' },
        checkbox: { enabled: false, label: 'I agree to receive emails.' }
      }
    }
  },
  {
    id: 'phone',
    label: 'Phone number',
    icon: 'phone',
    group: 'contact',
    inputType: 'phone',
    description: 'A phone number with country code and format checks.',
    facts: ['Saves in international format', 'Can sync to your email platform', 'Does not identify the visitor'],
    text: "What's the best number to reach you on?",
    defaults: { defaultCountry: 'us', mobileOnly: false, required: true }
  },
  {
    id: 'address',
    label: 'Address',
    icon: 'map-pin',
    group: 'contact',
    inputType: 'address',
    description: 'A street address with browser autofill.',
    facts: ['Saves street, city, region, postcode and country separately', 'No address lookup service'],
    text: "What's your address?",
    defaults: { required: false }
  },
  {
    id: 'country',
    label: 'Country',
    icon: 'globe-alt',
    group: 'contact',
    inputType: 'country',
    description: 'A country picker, pre-filled from the visitor’s location.',
    facts: ['Pre-selects the visitor’s detected country', 'Kept in Responses'],
    text: 'Which country do you live in?',
    defaults: { required: false }
  },
  {
    id: 'url',
    label: 'Website',
    icon: 'link',
    group: 'contact',
    inputType: 'url',
    description: 'A web address, checked for the right format.',
    facts: ['Checks the format', 'Saves as text'],
    text: "What's your company website?",
    defaults: { placeholder: 'https://', required: false }
  },
  {
    id: 'short',
    label: 'Short text',
    icon: 'bars-2',
    group: 'text',
    inputType: 'text',
    description: 'A single line of text.',
    facts: ['Saves as text', 'Shows in Responses'],
    text: 'What would you like us to know?',
    defaults: { placeholder: 'Type your answer', required: false }
  },
  {
    id: 'long',
    label: 'Long text',
    icon: 'bars-3-bottom-left',
    group: 'text',
    inputType: 'textarea',
    description: 'Multiple lines for longer answers.',
    facts: ['Saves as text', 'Righty groups answers into themes in Responses'],
    text: 'Tell us a bit about your situation',
    defaults: { placeholder: 'Type your answer', required: false }
  },
  {
    id: 'number',
    label: 'Number',
    icon: 'hashtag',
    group: 'text',
    inputType: 'number',
    description: 'A number, with optional min, max and units.',
    facts: [
      'Saves a number, including zero',
      'Optional prefix and suffix, like kg or hours',
      'Use the answer in flow conditions'
    ],
    text: 'How many do you need?',
    defaults: { numberType: 'plain', display: 'field', required: false }
  },
  {
    id: 'currency',
    label: 'Currency',
    icon: 'currency-dollar',
    group: 'text',
    inputType: 'number',
    description: 'A money amount in your currency.',
    facts: ['Saves a number', 'Defaults to USD'],
    text: "What's your budget?",
    defaults: { numberType: 'currency', currency: 'USD', display: 'field', required: false }
  },
  {
    id: 'date',
    label: 'Date',
    icon: 'calendar',
    group: 'text',
    inputType: 'date',
    description: 'A date picker.',
    facts: ['Saves a date as YYYY-MM-DD', 'Uses the visitor’s native date picker'],
    text: 'When would you like to start?',
    defaults: { required: false }
  },
  {
    id: 'rating',
    label: 'Rating',
    icon: 'star',
    group: 'scales',
    inputType: 'rating',
    description: 'A star rating, from 3 to 10 stars.',
    facts: ['Saves the score as a number', 'Advances on selection', 'No segments or branches'],
    text: 'How useful was this guide?',
    defaults: { ratingMax: 5, required: false }
  },
  {
    id: 'nps',
    label: 'NPS',
    icon: 'chart-bar',
    group: 'scales',
    inputType: 'nps',
    description: 'Net Promoter Score on a 0 to 10 scale.',
    facts: ['Saves the score as a number', 'Advances on selection', 'No segments or branches'],
    text: 'How likely are you to recommend us to a friend?',
    defaults: { minLabel: 'Not at all likely', maxLabel: 'Extremely likely', required: false }
  },
  {
    id: 'slider',
    label: 'Slider',
    icon: 'adjustments-horizontal',
    group: 'scales',
    inputType: 'number',
    description: 'Visitors drag to pick a value between your minimum and maximum.',
    facts: ['Saves a number', 'Starts at the middle or a value you choose', 'Optionally shows a % after the value'],
    text: 'How ready are you to get started?',
    defaults: {
      numberType: 'plain',
      display: 'slider',
      min: 0,
      max: 100,
      step: 1,
      minLabel: 'Not at all',
      maxLabel: 'Completely',
      required: false
    }
  }
]
export const questionTypeGroups: readonly {
  id: QuestionTypeGroup
  label: string
  typeIds: readonly QuestionTypeId[]
}[] = [
  { id: 'segment', label: 'Segment', typeIds: ['mc', 'yesno'] },
  { id: 'contact', label: 'Contact details', typeIds: ['name', 'email', 'phone', 'address', 'country', 'url'] },
  { id: 'text', label: 'Text & numbers', typeIds: ['short', 'long', 'number', 'currency', 'date'] },
  { id: 'scales', label: 'Scales', typeIds: ['rating', 'nps', 'slider'] }
]

export type QuestionSetting =
  | keyof QuestionInputSettings
  | 'multipleAnswers'
  | 'other'
  | 'randomize'
  | 'displayType'
  | 'hideLabels'
export const questionTypeSettings: Readonly<Record<QuestionTypeId, readonly QuestionSetting[]>> = {
  mc: ['displayType', 'hideLabels', 'multipleAnswers', 'other', 'randomize'],
  yesno: ['displayType', 'hideLabels', 'multipleAnswers', 'other', 'randomize'],
  name: ['nameFormat', 'partLabels', 'partPlaceholders', 'required', 'syncFieldMap'],
  email: ['placeholder', 'identify', 'contactAction', 'consent', 'suggestTypoFixes', 'required'],
  phone: ['placeholder', 'defaultCountry', 'mobileOnly', 'required'],
  address: ['partLabels', 'partPlaceholders', 'required', 'syncFieldMap'],
  country: ['placeholder', 'pinnedCountries', 'excludedCountries', 'detectCountry', 'required'],
  url: ['placeholder', 'required'],
  short: ['placeholder', 'required'],
  long: ['placeholder', 'required'],
  number: ['placeholder', 'min', 'max', 'step', 'prefix', 'suffix', 'required'],
  currency: ['placeholder', 'currency', 'min', 'max', 'required'],
  date: ['dateRange', 'dateRangeDays', 'required'],
  rating: ['ratingMax', 'required'],
  nps: ['minLabel', 'maxLabel', 'required'],
  slider: ['min', 'max', 'step', 'defaultValue', 'numberType', 'minLabel', 'maxLabel', 'required']
}

export function createQuestionDraft(typeId: QuestionTypeId): QuestionDraft {
  const type = questionTypes.find((entry) => entry.id === typeId)
  if (!type) {
    throw new Error(`Unknown question type: ${typeId}`)
  }
  const question: Question = {
    id: makeId(idPrefixes.RIGHT_ASK_QUESTION),
    text: type.text,
    description: '',
    isQuestion: true,
    dimensionId: null,
    displayType: type.inputType ? 'input' : 'buttons',
    displayOptions: {},
    inputType: type.inputType,
    inputSettings: structuredClone(type.defaults),
    isRandomizingOptionsOrder: false,
    otherOptions: null,
    options: []
  }
  if (type.inputType) {
    return { question, dimension: null }
  }
  const dimension: Dimension = {
    // An explicit exclusion list keeps this local factory independent of Pinia/config.
    id: makeId(idPrefixes.DIMENSION, [question.id]),
    name: type.label,
    semanticType: null,
    isMultiWinning: false,
    favorited: false,
    syncTo: null,
    syncToCustomField: null,
    syncMode: 'field',
    segments: [],
    signals: []
  }
  for (const label of type.answers ?? []) {
    const segment = { id: makeId(idPrefixes.SEGMENT), name: label }
    dimension.segments.push(segment)
    question.options.push({
      id: makeId(idPrefixes.RIGHT_ASK_QUESTION_OPTION),
      segmentId: segment.id,
      label,
      conditions: { or: [] },
      scores: []
    })
  }
  question.dimensionId = dimension.id
  return { question, dimension }
}

export function isLegacyOpenEnded(question: Question, dimension?: Dimension | null): boolean {
  return (
    question.displayType !== 'input' &&
    !!question.otherOptions?.enabled &&
    question.options.length === 2 &&
    !!dimension?.segments.some((segment) => segment.name === 'INT_OTHER')
  )
}

export function getQuestionType(question: Question, dimension?: Dimension | null): QuestionTypeDefinition {
  if (question.displayType !== 'input') {
    if (isLegacyOpenEnded(question, dimension)) {
      return legacy
    }
    const labels = question.options.map((option) => option.label.trim().toLowerCase())
    return (
      questionTypes.find(
        (type) => type.id === (labels.length === 2 && labels.includes('yes') && labels.includes('no') ? 'yesno' : 'mc')
      ) ?? multipleChoice
    )
  }
  if (question.inputType === 'number') {
    const settings = question.inputSettings
    const id = settings?.display === 'slider' ? 'slider' : settings?.numberType === 'currency' ? 'currency' : 'number'
    return questionTypes.find((type) => type.id === id) ?? multipleChoice
  }
  return questionTypes.find((type) => type.inputType === question.inputType) ?? multipleChoice
}

export function getQuestionButtonLabels(
  question: Question,
  dimension?: Pick<Dimension, 'isMultiWinning'> | null,
  widget?: Widget | null
) {
  const input = question.displayType === 'input'
  const autoAdvance = question.inputType === 'rating' || question.inputType === 'nps'
  const showNext = input
    ? !autoAdvance
    : question.displayType === 'radio' || !!dimension?.isMultiWinning || !!question.otherOptions?.enabled
  const showSkip = input && question.inputSettings?.required === false
  const nextDefault = widget?.options.nextButtonLabel || 'Next'
  const skipDefault = widget?.options.skipButtonLabel || 'Skip'
  const nextCustomized = !!question.inputSettings?.nextButtonLabel
  const skipCustomized = !!question.inputSettings?.skipButtonLabel
  return {
    showNext,
    showSkip,
    nextDefault,
    skipDefault,
    nextCustomized,
    skipCustomized,
    nextLabel: question.inputSettings?.nextButtonLabel || nextDefault,
    skipLabel: question.inputSettings?.skipButtonLabel || skipDefault,
    source: ((showNext && nextCustomized) || (showSkip && skipCustomized)
      ? 'customized for this question'
      : 'from the widget') as 'customized for this question' | 'from the widget'
  }
}
